/** @format */

import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { isCapexFieldVisible } from "./capex-validation";

const isBlank = (v) => v === undefined || v === null || v === "";

/**
 * Renders the Capex Calculator's Input Form — fields grouped by `category`, in `srNo` order,
 * as a 3-column table (Category | Field Label | Input value) mirroring the original Excel
 * workbook's "Category / Header / BD Input" layout. Field config comes entirely from
 * GET /v2/capex-calculator/input-form (no hardcoded fields).
 *
 * Props:
 *   fields      – the input-form field config array (see capex-validation.js for the shape)
 *   answers     – controlled { [fieldId]: value } map
 *   onChange    – (fieldId, value) => void
 *   showErrors  – when true, renders red border + helper text on empty mandatory fields
 */
const CapexInputForm = ({ fields = [], answers = {}, onChange, showErrors = false }) => {
  // Group fields by category, preserving the srNo-driven order the API already returned them in.
  const groups = useMemo(() => {
    const byCategory = new Map();
    const ordered = [];
    fields.forEach((field) => {
      if (!byCategory.has(field.category)) {
        const group = { category: field.category, fields: [] };
        byCategory.set(field.category, group);
        ordered.push(group);
      }
      byCategory.get(field.category).fields.push(field);
    });
    return ordered;
  }, [fields]);

  const setValue = (fieldId, value) => {
    if (!onChange) return;
    onChange(fieldId, value);
    // Clear any field whose showWhen depends on this one and is no longer satisfied, so a
    // stale answer from a now-hidden field doesn't silently ride along into inputAnswers.
    fields.forEach((f) => {
      if (
        f.showWhen?.fieldId === fieldId &&
        !(f.showWhen.valueIn || []).includes(value) &&
        !isBlank(answers[f.fieldId])
      ) {
        onChange(f.fieldId, undefined);
      }
    });
  };

  const renderInput = (field) => {
    const value = answers[field.fieldId] ?? "";
    const hasError = showErrors && field.isMandatory && isBlank(answers[field.fieldId]);

    if (field.inputType === "DROPDOWN") {
      const options = [...(field.options || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      return (
        <FormControl fullWidth size="small" error={hasError}>
          <Select
            displayEmpty
            value={value}
            onChange={(e) => setValue(field.fieldId, e.target.value)}
          >
            <MenuItem value="" disabled>
              Select...
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.value}
              </MenuItem>
            ))}
          </Select>
          {hasError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {field.fieldLabel} is required
            </Typography>
          )}
        </FormControl>
      );
    }

    // NUMBER
    return (
      <TextField
        type="number"
        size="small"
        fullWidth
        value={value}
        error={hasError}
        helperText={hasError ? `${field.fieldLabel} is required` : ""}
        onChange={(e) => setValue(field.fieldId, e.target.value)}
      />
    );
  };

  if (!fields.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No Input Form fields configured yet.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, width: "20%" }}>Category</TableCell>
            <TableCell sx={{ fontWeight: 700, width: "40%" }}>Field</TableCell>
            <TableCell sx={{ fontWeight: 700, width: "40%" }}>Input</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => {
            const visibleFields = group.fields.filter((f) => isCapexFieldVisible(f, answers));
            if (!visibleFields.length) return null;
            return visibleFields.map((field, idx) => (
              <TableRow key={field.fieldId}>
                {idx === 0 && (
                  <TableCell rowSpan={visibleFields.length} sx={{ fontWeight: 600, verticalAlign: "top" }}>
                    {group.category}
                  </TableCell>
                )}
                <TableCell>
                  {field.fieldLabel}
                  {field.isMandatory && (
                    <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
                      *
                    </Box>
                  )}
                </TableCell>
                <TableCell>{renderInput(field)}</TableCell>
              </TableRow>
            ));
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CapexInputForm;
